import { convertDistance, getDistance, getRhumbLineBearing } from 'geolib';
import { readString } from 'react-papaparse';

import { apiHits } from '../util/utility.js';

export function nearestIcaos(origin, icaodata, limit = 1500) {
  if (!icaodata[origin]) {
    throw new Error('The selected aircraft location is not in the airport database.');
  }
  const center = {
    latitude: icaodata[origin].lat,
    longitude: icaodata[origin].lon
  };
  return Object.keys(icaodata)
    .map(icao => [
      icao,
      getDistance(center, {
        latitude: icaodata[icao].lat,
        longitude: icaodata[icao].lon
      })
    ])
    .sort((a, b) => a[1] - b[1])
    .slice(0, limit)
    .map(([icao]) => icao);
}

export function cleanJobs(list, icaodata, settings, icaos = null) {
  const jobs = {};
  const minExpiration = new Date();
  if (settings.expiration !== '') {
    minExpiration.setTime(
      minExpiration.getTime() +
      parseInt(settings.expiration, 10) * 60 * 60 * 1000
    );
  }
  for (const job of list) {
    if (!parseInt(job.Pay, 10)) { continue; }
    const frIcao = job.Location;
    const toIcao = job.ToIcao ? job.ToIcao : job.Destination;
    const unit = job.UnitType ? job.UnitType : job.Units;

    if (!icaodata[frIcao] || !icaodata[toIcao]) { continue; }
    if (icaos !== null && !icaos.includes(toIcao)) { continue; }
    if (!settings.express && job.Express === 'True') { continue; }
    if (settings.expiration !== '') {
      const expire = new Date(`${job.ExpireDateTime.replace(' ', 'T')}Z`);
      if (expire < minExpiration) { continue; }
    }

    if (!jobs[frIcao]) {
      jobs[frIcao] = {};
    }
    if (!jobs[frIcao][toIcao]) {
      const from = { latitude: icaodata[frIcao].lat, longitude: icaodata[frIcao].lon };
      const to = { latitude: icaodata[toIcao].lat, longitude: icaodata[toIcao].lon };
      jobs[frIcao][toIcao] = [
        Math.round(getRhumbLineBearing(from, to)),
        Math.round(convertDistance(getDistance(from, to), 'sm')),
        {}
      ];
    }
    const leg = jobs[frIcao][toIcao][2];
    const unitType = unit === 'kg' ? 'k' : 'p';
    const assignmentType = ['true', 'True'].includes(job.PtAssignment)
      ? 'p'
      : (job.Type === 'Trip-Only' ? 't' : (job.Type === 'VIP' ? 'v' : 'a'));
    const key = unitType + assignmentType;
    if (!leg[key]) {
      leg[key] = [];
    }
    const assignment = [
      parseInt(job.Amount, 10),
      parseInt(job.Pay, 10),
      parseInt(job.Id, 10)
    ];
    if (job.Type === 'All-In') {
      assignment.push(parseInt(job.AircraftId, 10));
    }
    leg[key].push(assignment);
  }
  return jobs;
}

function checkRateLimit(hits, now) {
  if (hits.filter(hit => now - hit < 60 * 1000).length >= 10) {
    throw new Error('FSEconomy request limit reached. Try again in one minute.');
  }
  if (hits.filter(hit => now - hit < 6 * 60 * 60 * 1000).length >= 40) {
    throw new Error('FSEconomy request limit reached. Try again later.');
  }
}

export async function loadJobsAroundAircraft({key, origin, icaodata, settings, hits}) {
  if (!key) {
    throw new Error('Enter your Read Access Key in Load data first.');
  }
  checkRateLimit(hits, Date.now());
  const icaos = nearestIcaos(origin, icaodata);
  const url = 'data?userkey='+key+'&format=csv&query=icao&search=jobsfrom&icaos='+icaos.join('-');

  try {
    const response = await fetch(process.env.REACT_APP_PROXY+url);
    if (!response.ok) {
      throw new Error('FSEconomy returned status ' + response.status + '.');
    }
    const csv = await response.text();
    const parsed = readString(csv, {header: true, skipEmptyLines: 'greedy'});
    if (parsed.errors.length) {
      throw new Error('FSEconomy returned assignment data that could not be read.');
    }
    return cleanJobs(parsed.data, icaodata, settings);
  }
  finally {
    apiHits();
  }
}
